import React from 'react';

import { ResendState } from '../actions/reducer';
import { VerificationErrorCode } from '../../../../../auth/authTypes';

import styles from './TwoFactorAuthentication.module.scss';

import Icon from 'utils/components/icon';

interface TwoFactorAuthenticationFeedbackProps {
  isToManyResentCode: boolean;
  verifyCodeError: VerificationErrorCode;
  currentStatus: string;
  resendState: ResendState;
  onResend: () => void;
}

export const TwoFactorAuthenticationFeedback = ({
  isToManyResentCode,
  verifyCodeError,
  resendState,
  onResend
}: TwoFactorAuthenticationFeedbackProps) => {
  if (isToManyResentCode) {
    return (
      <ResendFeedbackContainer>
        You’ve reached the limit for resending codes. Try again later or{' '}
        <a href="mailto:publisherfirst@openweb.com" target="_blank" rel="noreferrer">
          Contact support
        </a>
      </ResendFeedbackContainer>
    );
  }

  if (verifyCodeError === 'Expired') {
    return resendState.loading ? <ResendLoadingState /> : <ExpiredCode onResend={onResend} />;
  }

  if (verifyCodeError === 'ToManyVerifyAttempts') {
    return <ToManyVerifyAttempts />;
  }

  return (
    <ResendFeedbackContainer>
      {resendState.loading ? (
        <ResendLoadingState />
      ) : resendState.success ? (
        <ResendSuccessState />
      ) : (
        <span>
          Didn't receive the email? <a onClick={onResend}>Resend</a>
        </span>
      )}
    </ResendFeedbackContainer>
  );
};

const ResendFeedbackContainer = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.resendFeedbackContainer}>{children}</div>
);

const ResendLoadingState = (): React.ReactElement => (
  <div className={styles.resendLoadingState}>
    <span>Sending new code to your email</span>
    <span className={styles.spinnerV2} />
  </div>
);

const ExpiredCode = ({ onResend }: { onResend: () => void }) => (
  <div className={styles.expiredCode}>
    <Icon name="circle-bolded-error" className={styles.inputCircleError} />
    Code has expired. <a onClick={onResend}>Send new code</a>
  </div>
);

const ToManyVerifyAttempts = (): React.ReactElement => (
  <div className={styles.toManyVerifyAttempts}>
    You’ve reached the maximum number of login attempts. For security reasons, we’ve temporarily blocked further
    attempts. Please try again later or{' '}
    <a href="mailto:publisherfirst@openweb.com" target="_blank" rel="noreferrer">
      Contact support
    </a>
  </div>
);

const ResendSuccessState = (): React.ReactElement => (
  <div className={styles.resendSuccessState}>
    Check your email <Icon name="checkmark" className={styles.checkmarkIcon} />
  </div>
);
